import { IFormItem } from '@components/Form/FormItem/FormItem';
import { Validators } from '@components/Form/FormItem/FormItemValidation';
import { DatePickerProps, FormInstance } from 'antd';
import dayjs from 'dayjs';

import { Dictionaries, DictMap } from '@/api/dictionaries/types';
import { OrganizationShort } from '@/api/organizations/types';
import { OrganizationLevelType } from '@/shared/organizations/enums';
import {
  IndividualSexLabel,
  IndividualStatus,
  IndividualStatusLabel,
} from '@/shared/vaccination/enums';
import { arrayToOptions, dictToOptions, enumToOptions } from '@/utils/utils';

const addCommonSchema = (
  level: OrganizationLevelType,
  birthdateColumn: IFormItem,
  dictionaries?: Dictionaries,
  uterus?: string[],
  structuals?: OrganizationShort[],
): IFormItem[] => [
  {
    label: 'Хозяйство',
    placeholder: 'Выберите хозяйство',
    key: 'organizationId',
    type: 'textarea',
    readonly: true,
    payload: {
      autoSize: true,
    },
  },
  {
    label: 'Структурное подразделение',
    placeholder: 'Выберите структурное подразделение',
    key: 'structuralDivisionId',
    type: 'select',
    payload: {
      options:
        structuals?.map((item) => ({
          label: item.name,
          value: item.id,
        })) || [],
    },
    validation:
      level === OrganizationLevelType.ORGANIZATION ? [Validators.required()] : [],
    hidden: level !== OrganizationLevelType.ORGANIZATION,
  },
  {
    label: 'Объект',
    placeholder: 'Выберите объект',
    key: 'individualLocationId',
    type: 'select',
    payload: {
      options: dictToOptions(dictionaries?.INDIVIDUAL_LOCATION),
    },
    validation: [Validators.required()],
  },
  {
    label: 'Инвентарный номер',
    placeholder: 'Введите инвентарный номер',
    key: 'inventoryNumber',
    type: 'text',
    validation: [Validators.required()],
  },
  {
    label: 'Инвентарный номер матки',
    placeholder: 'Введите инвентарный номер матки',
    key: 'uterusInventoryNumber',
    type: 'autocomplete',
    payload: {
      options: arrayToOptions(uterus),
    },
    validation: [Validators.required()],
  },
  birthdateColumn,
  {
    label: 'Статус',
    placeholder: 'Выберите статус',
    key: 'individualStatus',
    type: 'select',
    payload: {
      options: enumToOptions(IndividualStatusLabel),
    },
    validation: [Validators.required()],
  },
  {
    label: 'Заболевание',
    placeholder: 'Введите заболевание',
    key: 'diseaseDescription',
    type: 'text',
    validation: (form) =>
      form.getFieldValue('individualStatus') !== IndividualStatus.SICK
        ? []
        : [Validators.required()],
    hidden: (form) => {
      return form.getFieldValue('individualStatus') !== IndividualStatus.SICK;
    },
    payload: {
      maxLength: 250,
    },
  },
  {
    label: 'Дата падежа/выбытия',
    placeholder: 'Введите дату падежа/выбытия',
    key: 'deadRemovalDate',
    type: 'date',
    payload: {
      disabledDate: (current: DatePickerProps['value']) => {
        if (!current) return false;

        const today = dayjs().startOf('day');
        return current > today.endOf('day');
      },
    },
    validation: (form) =>
      ![IndividualStatus.DEAD, IndividualStatus.REMOVAL].includes(
        form.getFieldValue('individualStatus'),
      )
        ? []
        : [Validators.required()],
    hidden: (form) => {
      return ![IndividualStatus.DEAD, IndividualStatus.REMOVAL].includes(
        form.getFieldValue('individualStatus'),
      );
    },
  },
  {
    label: 'Вид животного',
    placeholder: 'Выберите вид животного',
    key: 'animalSpeciesId',
    type: 'select',
    payload: {
      options: dictToOptions(dictionaries?.ANIMAL_SPECIES),
    },
    validation: [Validators.required()],
  },
  {
    label: 'Пол',
    placeholder: 'Выберите пол',
    key: 'sex',
    type: 'select',
    payload: {
      options: enumToOptions(IndividualSexLabel),
    },
    validation: [Validators.required()],
  },
  {
    label: 'Животная масса (кг)',
    placeholder: 'Введите живую массу (кг)',
    key: 'liveWeight',
    type: 'number',
    validation: [Validators.required()],
  },
];

export const addSchema = (
  level: OrganizationLevelType,
  dictionaries?: Dictionaries,
  uterus?: string[],
  structuals?: OrganizationShort[],
): IFormItem[] => [
  ...addCommonSchema(
    level,
    {
      label: 'Дата рождения',
      placeholder: 'Введите дату рождения',
      key: 'birthDate',
      type: 'date',
      payload: {
        disabledDate: (current: DatePickerProps['value']) => {
          if (!current) return false;

          const today = dayjs().startOf('day');
          const minDate = today.subtract(3, 'day');
          return current > today.endOf('day') || current < minDate.startOf('day');
        },
      },
      validation: [Validators.required()],
    },
    dictionaries,
    uterus,
    structuals,
  ),
  {
    label: 'Мероприятия нулевого дня выполнены',
    key: 'eventsOfFirstDayCompleted',
    type: 'radioGroup',
    validation: [Validators.required()],
    payload: {
      direction: 'horizontal',
      options: [
        { value: true, label: 'Да' },
        { value: false, label: 'Нет' },
      ],
    },
  },
];

export const addWithEventsSchema = (
  level: OrganizationLevelType,
  getCompletedEvents: (birthdate: string, form: FormInstance) => void,
  dataRequest: any,
  dictionaries?: Dictionaries & DictMap,
  uterus?: string[],
  structuals?: OrganizationShort[],
): IFormItem[] => [
  ...addCommonSchema(
    level,
    {
      label: 'Дата рождения',
      placeholder: 'Введите дату рождения',
      key: 'birthDate',
      type: 'date',
      onChange: getCompletedEvents,
      payload: {
        disabledDate: (current: DatePickerProps['value']) => {
          if (!current) return false;

          const today = dayjs().startOf('day');
          return current > today.endOf('day');
        },
      },
      validation: [Validators.required()],
    },
    dictionaries,
    uterus,
    structuals,
  ),
  {
    label: 'Проведенные мероприятия',
    key: 'completedEvents',
    type: 'protocolsSelect',
    payload: {
      dataRequest,
    },
  },
];
